<template>
  <div class="page-wrapper">
    <div class="content-wrapper">
      <loading v-model:active="isLoading" :is-full-page="true" />

      <img class="uni-logo" :src="`${publicPath}assets/logo.png`" />

      <h1>Account Management</h1>

      <div class="tabs">
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'profile' }" 
          @click="activeTab = 'profile'"
        >
          Profile Information
        </button>
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'password' }" 
          @click="activeTab = 'password'"
        >
          Change Password
        </button>
      </div>

      <div v-if="activeTab === 'profile'">
        <user-profile-form
          :existing-user="userData"
          :is-registration="false"
          :show-password-fields="false"
          submit-button-text="Update Profile"
          @validation-error="handleValidationError"
          @submit="handleProfileUpdate"
          @cancel="goBack"
        />
      </div>

      <div v-if="activeTab === 'password'">
        <form v-on:submit.prevent="handlePasswordChange">
          <div class="form-group">
            <label>Current Password</label>
            <input type="password" v-model="passwordData.oldPassword" />
          </div>

          <div class="form-group">
            <label>New Password</label>
            <div class="password-group">
              <input :type="passwordVisibility" v-model="passwordData.newPassword" />
              <i v-if="passwordVisibility === 'password'" class="far fa-eye-slash" @click="togglePasswordVisibility"></i>
              <i v-if="passwordVisibility === 'text'" class="far fa-eye" @click="togglePasswordVisibility"></i>
            </div>
          </div>

          <div class="form-group">
            <label>Confirm New Password</label>
            <div class="password-group">
              <input :type="passwordVisibility" v-model="passwordData.confirmPassword" />
              <i v-if="passwordVisibility === 'password'" class="far fa-eye-slash" @click="togglePasswordVisibility"></i>
              <i v-if="passwordVisibility === 'text'" class="far fa-eye" @click="togglePasswordVisibility"></i>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn">Change Password</button>
            <button type="button" class="btn btn-secondary" @click="goBack">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { useToast } from "vue-toastification";
import authStore from "../store/auth-store";
import UserProfileForm from '../components/UserProfileForm.vue';

export default {
  setup() {
    const toast = useToast();
    return { toast };
  },

  components: {
    Loading,
    UserProfileForm
  },

  data() {
    return {
      isLoading: false,
      activeTab: 'profile',
      userData: null,
      passwordData: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
      },
      passwordVisibility: 'password'
    };
  },

  async mounted() {
    this.isLoading = true;
    try {
      await this.fetchUserData();
    } catch (error) {
      this.toast.error("Failed to load user data");
      console.error("Error fetching user data:", error);
    } finally {
      this.isLoading = false;
    }
  },

  computed: {
    user() {
      return authStore.state.user;
    }
  },

  methods: {
    async fetchUserData() {
      try {
        const response = await this.axios.get("/api/auth/getuserdetails", {
          headers: { 'Authorization': 'Bearer ' + this.user.token }
        });
        console.log(JSON.parse(JSON.stringify(response.data)));
        this.userData = response.data;
      } catch (error) {
        console.error("Error fetching user details:", error);
        throw error;
      }
    },

    togglePasswordVisibility() {
      this.passwordVisibility = this.passwordVisibility === 'password' ? 'text' : 'password';
    },

    handleValidationError(message) {
      this.toast.error(message);
    },

    async handleProfileUpdate(profileData) {
      this.isLoading = true;
      try {
        await this.axios.post("/api/auth/updateprofile", profileData, {
          headers: { 'Authorization': 'Bearer ' + this.user.token }
        });
        this.toast.success("Profile updated successfully");
        await this.fetchUserData();
      } catch (error) {
        this.toast.error(error.response?.data || "Failed to update profile");
        console.error("Error updating profile:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async handlePasswordChange() {
      if (!this.passwordData.oldPassword || !this.passwordData.newPassword || !this.passwordData.confirmPassword) {
        this.toast.error("Please fill in all password fields");
        return;
      }

      if (this.passwordData.newPassword !== this.passwordData.confirmPassword) {
        this.toast.error("New passwords do not match");
        return;
      }

      this.isLoading = true;
      
      try {
        const dto = {
          oldPassword: this.passwordData.oldPassword,
          newPassword: this.passwordData.newPassword
        };

        await this.axios.post("/api/auth/changepassword", dto, {
          headers: { 'Authorization': 'Bearer ' + this.user.token }
        });

        this.toast.success("Password changed successfully");
        
        this.passwordData = {
          oldPassword: "",
          newPassword: "",
          confirmPassword: ""
        };

        this.activeTab = 'profile';
        
      } catch (error) {
        this.toast.error(error.response?.data || "Failed to change password");
        console.error("Error changing password:", error);
      } finally {
        this.isLoading = false;
      }
    },
    
    goBack() {
      this.$router.push('/');
    }
  }
}
</script>

<style lang="scss" scoped>
.page-wrapper {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.content-wrapper {
  padding: 20px;
  
  img.uni-logo {
    max-width: 200px;
    margin: 0 auto 20px;
    display: block;
  }

  h1 {
    font-size: 24px;
    margin-bottom: 20px;
    text-align: center;
  }

  .tabs {
    display: flex;
    margin-bottom: 20px;
    border-bottom: 1px solid #ddd;
    
    .tab-button {
      padding: 10px 20px;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 16px;
      
      &.active {
        border-bottom: 2px solid #007bff;
        font-weight: bold;
      }
      
      &:hover {
        background-color: #f5f5f5;
      }
    }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;

    label {
      font-weight: bold;
      margin-bottom: 5px;
    }

    input {
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
    }
  }

  .password-group {
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding-right: 8px;

    input {
      flex: 1;
      border: none;
      padding: 8px;
      &:focus {
        outline: none;
      }
    }

    i {
      cursor: pointer;
      color: #666;
    }
  }

  .form-actions {
    margin-top: 20px;
    display: flex;
    gap: 10px;
    
    .btn {
      padding: 8px 15px;
      border-radius: 4px;
      cursor: pointer;
      
      &.btn-secondary {
        background-color: #f1f1f1;
        color: #333;
      }
    }
  }
}
</style>

<!-- <template>
  <div class="page-wrapper">
    <div class="content-wrapper">
      <loading v-model:active="isLoading" :is-full-page="true"/>

      <img class="uni-logo" src="/assets/logo.png"/>

      <h1>Reset Password</h1>

      <form v-on:submit.prevent="onSubmit">
        <div class="form-group">
          <label>Old Password</label>
          <input type="password" v-model="oldPassword"/>
        </div>

        <div class="form-group">
          <label>New Password</label>
          <input type="password" v-model="newPassword"/>
        </div>

        <div class="form-group">
          <label>Confirm Password</label>
          <input type="password" v-model="confirmPassword"/>
        </div>

        <button type="submit" class="btn">Reset</button>
      </form>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { useToast } from "vue-toastification";
import authStore from "../store/auth-store";

export default {
   setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      isLoading: false,
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  },

  methods: {
    async onSubmit() {
      if (this.oldPassword == "" || this.newPassword == "" || this.confirmPassword == "") {
        this.toast.error("Enter passwords");
        return; 
      }

      if (this.newPassword != this.confirmPassword) {
        this.toast.error("Passwords do not match");
        return; 
      }


      this.isLoading = true;

      var dto = {
        oldPassword: this.oldPassword,
        newPassword: this.newPassword
      };

      try {
        var response = await this.axios.post("/api/auth/changepassword", dto, { headers: { 'Authorization' : 'Bearer ' + this.user.token }});
        console.log(response);
        this.isLoading = false;
        this.toast.success("Successfully reset password");
        this.$router.push("/");

      } catch (e) {
        //this.$toast.open('Invalid username/password');
        this.toast.error("Invalid old password");
        this.isLoading = false;
      }
    }
  },

  computed: {
    user() {
      return authStore.state.user;
    }
  },
  components: {
    Loading
  },
}
</script>

<style lang="scss" scoped>
  .content-wrapper {
    padding: 10px;
    img {
      max-width: 90%;
      margin: 15px auto;
    }

    h1 {
      font-family: 'Source Sans Pro', sans-serif;
      font-size: 26px;
      line-height: 36px;
    }

    form {
      margin-top: 20px;
      .form-group {
        display: flex;
        flex-direction: column;
        margin-bottom: 10px;

        label {
          font-weight: bold;
          text-align: left;
          margin-bottom: 5px;
        }

        input {
          padding: 5px 8px;
        }
      }
    }
  }
</style> -->
